import { EventEmitter } from 'events';
import {
  APIErrorTimeout,
  type HomeyAPI,
  type Manager,
  type RealtimeConnectionLease,
} from 'homey-api';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import type { Deferred } from '../lib/defer';
import { ToastManager } from '../ToastManager';

import { selectedHomeyStore } from './SelectedHomeyStore';

export type FetchPolicy = 'reset' | 'refresh' | 'background';

export type FetchArgs = {
  api: HomeyAPI;
  fetchPolicy: FetchPolicy;
  cache: boolean;
  abortController: AbortController;
};

export type ReconnectArgs = {
  api: HomeyAPI;
  disconnectDurationMs: number;
};

type BaseApiStoreState = {
  isLoading: boolean;
  error: unknown;
};

export abstract class BaseApiStore<TState extends BaseApiStoreState, TItem = unknown> {
  DEBUG = false;

  store: ReturnType<BaseApiStore<TState, TItem>['createStore']>;
  apiUnsubscribe!: () => void;
  pending: { [key: string]: Deferred<void> } = {};
  abortController: AbortController | null = null;
  emitter: EventEmitter;

  mounts: Set<unknown> = new Set();
  timeout: ReturnType<typeof setTimeout> | null = null;
  api: HomeyAPI | null = null;
  fetched: boolean = false;
  connectionEpoch: number = 0;
  longDisconnectMs: number = 30 * 1000;

  key: string;
  // devtools?: boolean;

  processBatchTimeout: null | ReturnType<typeof setTimeout> = null;
  updateQueue: { [key: string]: TItem } = {};
  managerConnectionLeasePromise: Promise<RealtimeConnectionLease> | null = null;

  constructor(key: string) {
    this.key = key;
    this.store = this.createStore();
    this.emitter = new EventEmitter();
  }

  abstract createInitialState(): TState;

  abstract onAttached?({ api }: { api: HomeyAPI }): void;

  abstract fetch(args: FetchArgs): Promise<void>;

  abstract destroy(arg: string): void;

  abstract onDetached?({ api }: { api: HomeyAPI }): void;

  shouldRefreshAfterReconnect?({ api, disconnectDurationMs }: ReconnectArgs): boolean;

  setFetchError({ error, fetchPolicy }: { error: unknown; fetchPolicy: FetchPolicy }) {
    if (fetchPolicy === 'background') {
      ToastManager.handleError(error);
      this.set({
        ...this.get(),
        isLoading: false,
      });
      return;
    }

    if (fetchPolicy === 'refresh') {
      this.set({
        ...this.get(),
        isLoading: false,
        error,
      });
      return;
    }

    this.set({
      ...this.createInitialState(),
      isLoading: false,
      error,
    });
  }

  async connectManager({ manager }: { manager: Manager }) {
    const existingLeasePromise = this.managerConnectionLeasePromise;

    if (existingLeasePromise != null) {
      await existingLeasePromise;
      return;
    }

    const leasePromise = BaseApiStore.#acquireManagerConnection(manager);
    this.managerConnectionLeasePromise = leasePromise;

    try {
      await leasePromise;
    } catch (error) {
      if (this.managerConnectionLeasePromise === leasePromise) {
        this.managerConnectionLeasePromise = null;
      }

      throw error;
    }
  }

  async disconnectManager() {
    const leasePromise = this.managerConnectionLeasePromise;

    if (leasePromise == null) {
      return;
    }

    this.managerConnectionLeasePromise = null;
    const lease = await leasePromise;
    await lease.release();
  }

  static async #acquireManagerConnection(manager: Manager): Promise<RealtimeConnectionLease> {
    try {
      return await manager.acquireConnection();
    } catch (error) {
      if (!(error instanceof APIErrorTimeout)) {
        throw error;
      }

      return await manager.acquireConnection();
    }
  }

  selectApi(state: {
    api: HomeyAPI | null;
    apiSilent: boolean;
    connectionEpoch: number;
    lastDisconnectDurationMs: number;
  }) {
    return {
      api: state.api,
      apiSilent: state.apiSilent,
      connectionEpoch: state.connectionEpoch,
      lastDisconnectDurationMs: state.lastDisconnectDurationMs,
    };
  }

  onApi({
    api,
    apiSilent,
    connectionEpoch,
    lastDisconnectDurationMs,
  }: {
    api: HomeyAPI | null;
    apiSilent: boolean;
    connectionEpoch: number;
    lastDisconnectDurationMs: number;
  }) {
    this.abortController?.abort();
    const didApiChange = this.api !== api;
    const didReconnect =
      didApiChange === false && api != null && connectionEpoch !== this.connectionEpoch;

    if (didApiChange) {
      if (this.api != null) {
        this.onDetached?.({ api: this.api });
      }
      this.DEBUG && console.info(`destroy:${this.key}:onApi`);

      this.destroy?.('onApi');
      this.api = api;

      if (this.api != null) {
        this.api.on('destroyed', () => {
          this.abortController?.abort();
        });
      }
      this.fetched = false;
      this.connectionEpoch = connectionEpoch;
    } else if (didReconnect) {
      this.connectionEpoch = connectionEpoch;
    }

    clearTimeout(this.processBatchTimeout as unknown as number);
    this.processBatchTimeout = null;
    this.updateQueue = {};

    if (api != null) {
      if (this.mounts.size > 0) {
        if (didApiChange) {
          this.fetched = true;

          // TODO
          // this should be enforced it can only ever be called once for a api instance.
          this.onAttached?.({ api });
          this.DEBUG && console.info(`fetch:${this.key}`);

          const fetchPolicy: FetchPolicy = apiSilent === true ? 'background' : 'reset';

          this.abortController = new AbortController();
          this.fetch?.({
            api,
            fetchPolicy,
            cache: false,
            abortController: this.abortController,
          }).catch(console.error);

          return;
        }

        if (didReconnect) {
          const shouldRefreshAfterReconnect =
            this.shouldRefreshAfterReconnect?.({
              api,
              disconnectDurationMs: lastDisconnectDurationMs,
            }) ?? lastDisconnectDurationMs >= this.longDisconnectMs;

          if (shouldRefreshAfterReconnect) {
            this.fetched = true;
            this.DEBUG && console.info(`fetch:${this.key}:reconnect`);
            this.abortController = new AbortController();
            this.fetch?.({
              api,
              fetchPolicy: 'background',
              cache: false,
              abortController: this.abortController,
            }).catch(console.error);
          }

          return;
        }
      } else if (didApiChange) {
        this.store.setState(this.createInitialState());
      }
    } else {
      this.store.setState(this.createInitialState());
    }
  }

  createStore() {
    const store = createWithEqualityFn<TState>()(
      devtools(
        subscribeWithSelector(() => this.createInitialState()),
        {
          name: this.key,
        },
      ),
      Object.is,
    );

    this.store = store;

    this.apiUnsubscribe = selectedHomeyStore.store.subscribe(
      this.selectApi.bind(this),
      this.onApi.bind(this),
      {
        equalityFn: shallow,
        fireImmediately: true,
      },
    );

    return store;
  }

  attach(ref: unknown) {
    clearTimeout(this.timeout as unknown as number);
    this.mounts.add(ref);

    if (this.fetched === false && this.api != null && this.api.isDestroyed() === false) {
      this.fetched = true;
      this.onAttached?.({ api: this.api });
      clearTimeout(this.processBatchTimeout as unknown as number);
      this.processBatchTimeout = null;
      this.updateQueue = {};
      this.DEBUG && console.info(`fetch:${this.key}`);
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.fetch?.({
        api: this.api,
        fetchPolicy: 'reset',
        cache: false,
        abortController: this.abortController,
      }).catch(console.error);
    }
  }

  detach(ref: unknown) {
    this.mounts.delete(ref);

    if (this.mounts.size === 0) {
      // Destroy after 1 mins.
      this.timeout = setTimeout(() => {
        if (this.api != null) {
          this.onDetached?.({ api: this.api });
        }
        clearTimeout(this.processBatchTimeout as unknown as number);
        this.processBatchTimeout = null;
        this.updateQueue = {};
        this.destroy?.('detached');
        this.fetched = false;
        this.store.setState(this.createInitialState());
      }, 60 * 1000);
    }
  }

  get get() {
    return this.store.getState;
  }

  get set() {
    return this.store.setState;
  }

  getApi() {
    if (this.api == null) throw new Error('Missing Api');

    return this.api;
  }

  async retry(args?: { fetchPolicy?: FetchPolicy; cache?: boolean }) {
    this.abortController?.abort();

    if (this.api != null) {
      clearTimeout(this.processBatchTimeout as unknown as number);
      this.processBatchTimeout = null;
      this.updateQueue = {};

      this.DEBUG && console.info(`fetch:${this.key}`);
      this.abortController = new AbortController();
      return this.fetch({
        fetchPolicy: args?.fetchPolicy ?? 'reset',
        cache: args?.cache ?? false,
        api: this.api,
        abortController: this.abortController,
      }).catch(console.error);
    }
  }

  async refresh(args?: { cache?: boolean }) {
    return this.retry({
      fetchPolicy: 'refresh',
      cache: args?.cache ?? false,
    });
  }

  subscribe<Selection>(
    selector: (state: TState) => Selection,
    callback: (selection: Selection) => void,
  ) {
    const ref = {};
    this.attach(ref);
    const unsub = this.store.subscribe(selector, callback, {
      fireImmediately: true,
      equalityFn: shallow,
    });

    return () => {
      unsub();
      this.detach(ref);
    };
  }

  batch(callback: (updateQueue: { [key: string]: TItem }) => void) {
    if (this.processBatchTimeout == null) {
      this.processBatchTimeout = setTimeout(() => {
        const currentQueue = this.updateQueue;
        this.updateQueue = {};
        this.processBatchTimeout = null;
        callback(currentQueue);
      }, 200);
    }
  }

  addToQueue(key: string, value: TItem) {
    this.updateQueue[key] = value;
  }
}
